import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Minus, Plus, Heart, ShoppingCart, Truck, Shield, RotateCcw, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import ProductCard from '../components/ui/ProductCard';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const { addToCart, loading: cartLoading } = useCart();

  // Review form
  const [reviewForm, setReviewForm] = useState({ name: '', rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const [productRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/products/${id}`),
          axios.get(`${API}/reviews/${id}`)
        ]);

        setProduct(productRes.data);
        setReviews(reviewsRes.data);

        // Fetch related products
        const relatedRes = await axios.get(`${API}/products/category/${productRes.data.category}`);
        setRelatedProducts(relatedRes.data.filter(p => p.id !== id).slice(0, 4));
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    window.scrollTo(0, 0);
  }, [id]);

  const handleAddToCart = async () => {
    const success = await addToCart(product.id, quantity);
    if (success) {
      toast.success(`${product.name} added to cart!`);
    } else {
      toast.error('Failed to add to cart');
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.name || !reviewForm.comment) return;

    setSubmittingReview(true);
    try {
      const response = await axios.post(`${API}/reviews`, {
        product_id: id,
        ...reviewForm
      });
      setReviews([response.data, ...reviews]);
      setReviewForm({ name: '', rating: 5, comment: '' });
      toast.success('Review submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8E7] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1F7A63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#FFF8E7] flex flex-col items-center justify-center">
        <p className="text-xl text-[#574021] mb-4">Product not found</p>
        <Link to="/shop" className="btn-primary">
          Back to Shop
        </Link>
      </div>
    );
  }

  const allImages = [product.image, ...(product.images || [])];
  const discountPercentage = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-[#FFF8E7]" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-[#6B4F2A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center gap-2 text-sm text-[#574021]" data-testid="product-breadcrumb">
            <Link to="/" className="hover:text-[#1F7A63] transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 text-[#6B4F2A]/30" />
            <Link to="/shop" className="hover:text-[#1F7A63] transition-colors">Shop</Link>
            <ChevronRight className="w-4 h-4 text-[#6B4F2A]/30" />
            <Link to={`/shop?category=${product.category}`} className="hover:text-[#1F7A63] transition-colors capitalize">
              {product.category.replace('-', ' ')}
            </Link>
            <ChevronRight className="w-4 h-4 text-[#6B4F2A]/30" />
            <span className="text-[#2C1E0F] font-medium truncate max-w-[150px]">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Section */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Image Gallery */}
            <div className="space-y-4" data-testid="product-gallery">
              <div className="aspect-square bg-white rounded-3xl overflow-hidden border border-[#6B4F2A]/10">
                <img
                  src={allImages[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              </div>
              
              {allImages.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {allImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`gallery-thumb flex-shrink-0 ${selectedImage === index ? 'active' : ''}`}
                      data-testid={`gallery-thumb-${index}`}
                    >
                      <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              {/* Badges */}
              <div className="flex gap-2">
                {discountPercentage > 0 && (
                  <span className="badge badge-sale">-{discountPercentage}% OFF</span>
                )}
                {product.is_bestseller && (
                  <span className="badge badge-bestseller">Bestseller</span>
                )}
              </div>

              {/* Category */}
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#1F7A63]" data-testid="product-detail-category">
                {product.category.replace('-', ' ')}
              </p>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight" data-testid="product-detail-title">
                {product.name}
              </h1>

              {/* Rating */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < Math.floor(product.rating) ? 'text-[#E0A106] fill-[#E0A106]' : 'text-[#6B4F2A]/30'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-[#574021]" data-testid="product-detail-rating">
                  {product.rating} ({product.reviews_count} reviews)
                </span>
              </div>

              {/* Price */}
              <div className="flex items-center gap-4">
                <span className="text-3xl font-bold text-[#1F7A63]" data-testid="product-detail-price">
                  Rs. {product.price.toLocaleString()}
                </span>
                {product.original_price && (
                  <span className="text-xl text-[#574021]/50 line-through">
                    Rs. {product.original_price.toLocaleString()}
                  </span>
                )}
              </div>

              {/* Weight */}
              <p className="text-[#574021]" data-testid="product-detail-weight">
                Weight: {product.weight}
              </p>

              {/* Description */}
              <p className="text-[#574021] leading-relaxed" data-testid="product-detail-description">
                {product.description}
              </p>

              {/* Quantity Selector */}
              <div className="flex items-center gap-6">
                <span className="font-medium text-[#2C1E0F]">Quantity:</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="qty-btn"
                    data-testid="quantity-decrease-btn"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-12 text-center font-semibold text-[#2C1E0F]" data-testid="quantity-value">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="qty-btn"
                    data-testid="quantity-increase-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  onClick={handleAddToCart}
                  disabled={cartLoading}
                  className="flex-1 btn-primary flex items-center justify-center gap-2 py-4"
                  data-testid="add-to-cart-button"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartLoading ? 'Adding...' : 'Add to Cart'}
                </button>
                <button
                  className="w-14 h-14 flex items-center justify-center border-2 border-[#6B4F2A]/20 rounded-full hover:border-[#E0A106] hover:text-[#E0A106] transition-colors"
                  data-testid="add-to-wishlist-btn"
                >
                  <Heart className="w-5 h-5" />
                </button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-[#6B4F2A]/10">
                <div className="text-center">
                  <Truck className="w-6 h-6 mx-auto text-[#1F7A63] mb-2" />
                  <p className="text-xs text-[#574021]">Free Shipping<br />Over Rs. 3000</p>
                </div>
                <div className="text-center">
                  <Shield className="w-6 h-6 mx-auto text-[#1F7A63] mb-2" />
                  <p className="text-xs text-[#574021]">100% Pure<br />Guaranteed</p>
                </div>
                <div className="text-center">
                  <RotateCcw className="w-6 h-6 mx-auto text-[#1F7A63] mb-2" />
                  <p className="text-xs text-[#574021]">Easy Returns<br />7 Days</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-8 bg-white border-t border-[#6B4F2A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Headers */}
          <div className="flex gap-8 border-b border-[#6B4F2A]/10 mb-8">
            <button
              onClick={() => setActiveTab('description')}
              className={`pb-4 font-medium transition-colors ${
                activeTab === 'description'
                  ? 'text-[#1F7A63] border-b-2 border-[#1F7A63]'
                  : 'text-[#574021] hover:text-[#1F7A63]'
              }`}
              data-testid="tab-description"
            >
              Description
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`pb-4 font-medium transition-colors ${
                activeTab === 'reviews'
                  ? 'text-[#1F7A63] border-b-2 border-[#1F7A63]'
                  : 'text-[#574021] hover:text-[#1F7A63]'
              }`}
              data-testid="tab-reviews"
            >
              Reviews ({reviews.length})
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'description' && (
            <div className="prose max-w-none" data-testid="tab-content-description">
              <p className="text-[#574021] leading-relaxed">
                {product.description}
              </p>
              <h3 className="text-[#2C1E0F] font-bold mt-6 mb-3">Product Features</h3>
              <ul className="list-disc list-inside text-[#574021] space-y-2">
                <li>100% pure and organic</li>
                <li>No artificial additives or preservatives</li>
                <li>Sourced from premium farms</li>
                <li>Lab tested for quality assurance</li>
                <li>Freshly packed for maximum freshness</li>
              </ul>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div data-testid="tab-content-reviews">
              {/* Review Form */}
              <div className="bg-[#FFF8E7] rounded-2xl p-6 mb-8">
                <h3 className="font-bold text-[#2C1E0F] mb-4">Write a Review</h3>
                <form onSubmit={handleSubmitReview} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#574021] mb-1">Your Name</label>
                    <input
                      type="text"
                      value={reviewForm.name}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, name: e.target.value }))}
                      className="input-field"
                      required
                      data-testid="review-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#574021] mb-1">Rating</label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setReviewForm(prev => ({ ...prev, rating: star }))}
                          data-testid={`review-star-${star}`}
                        >
                          <Star
                            className={`w-6 h-6 transition-colors ${
                              star <= reviewForm.rating ? 'text-[#E0A106] fill-[#E0A106]' : 'text-[#6B4F2A]/30'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#574021] mb-1">Your Review</label>
                    <textarea
                      value={reviewForm.comment}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                      rows={4}
                      className="input-field resize-none"
                      required
                      data-testid="review-comment-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submittingReview}
                    className="btn-primary"
                    data-testid="review-submit-btn"
                  >
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </button>
                </form>
              </div>

              {/* Reviews List */}
              <div className="space-y-6">
                {reviews.length > 0 ? (
                  reviews.map((review) => (
                    <div key={review.id} className="bg-white rounded-2xl border border-[#6B4F2A]/10 p-6" data-testid={`review-${review.id}`}>
                      <div className="flex items-center gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i < review.rating ? 'text-[#E0A106] fill-[#E0A106]' : 'text-[#6B4F2A]/30'
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-[#574021] mb-3">{review.comment}</p>
                      <p className="text-sm text-[#574021]/70">
                        — {review.name}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-[#574021] py-8">No reviews yet. Be the first to review!</p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="section" data-testid="related-products-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-black text-[#2C1E0F] tracking-tight mb-8">
              Related Products
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {relatedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetailPage;
